import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updatePriceInstantly);
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updatePriceInstantly);
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Instantly updates the price using pre-formatted per-variant data embedded in
   * the variant picker. Runs on `variant:selected` (dispatched synchronously on click)
   * so the shopper sees the new price before the full section re-render lands.
   *
   * Silently bails out if the required data isn't present (e.g. on quick-add,
   * product cards, or volume-pricing products) so the existing fetch/morph
   * pathway still takes over.
   *
   * @param {Event} event
   */
  updatePriceInstantly = (event) => {
    const variantPicker = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('variant-picker') : null
    );
    if (!variantPicker) return;

    const pricesScript = /** @type {HTMLScriptElement | null} */ (
      variantPicker.querySelector('script[data-variant-prices]')
    );
    if (!pricesScript?.textContent) return;

    const checkedRadio = /** @type {HTMLInputElement | null} */ (variantPicker.querySelector('input:checked'));
    const variantId = checkedRadio?.dataset.variantId;
    if (!variantId) return;

    let variants;
    try {
      variants = JSON.parse(pricesScript.textContent);
    } catch {
      return;
    }
    const variant = Array.isArray(variants) ? variants.find((v) => String(v.id) === String(variantId)) : null;
    if (!variant) return;

    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    // Skip volume-pricing range display — let the server refresh handle it.
    if (priceContainer.querySelector('.price__regular, .price__sale') == null) return;

    const perMtr = pricesScript.dataset.perMtr === 'true';
    const unitSuffix = perMtr
      ? `<span class="fp-price-unit" style="font-size:0.65em;font-weight:300;color:#8C8279;margin-left:3px;">/${
          variant.title === 'By meter' ? 'mtr' : 'pc'
        }</span>`
      : '';

    const priceRegular = priceContainer.querySelector('.price__regular');
    const priceSale = priceContainer.querySelector('.price__sale');
    const regularPriceEl = priceContainer.querySelector('.price__regular .price-item--regular.price');
    const salePriceEl = priceContainer.querySelector('.price__sale .price-item--sale.price');
    const compareAtEls = priceContainer.querySelectorAll('.compare-at-price');

    if (regularPriceEl) regularPriceEl.innerHTML = `${variant.price}${unitSuffix}`;
    if (salePriceEl) salePriceEl.innerHTML = `${variant.price}${unitSuffix}`;
    compareAtEls.forEach((el) => {
      el.innerHTML = variant.compare_at_price;
    });

    if (priceRegular && priceSale) {
      priceRegular.classList.toggle('price__hidden', !!variant.on_sale);
      priceSale.classList.toggle('price__hidden', !variant.on_sale);
    }
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
